/**
 * virtourCatalog.js — VirTour Media Product Database (VIRTOUR_DB)
 *
 * Mirror of VirTour Media's agent order form (https://agent.virtourmedia.com/order).
 * Roto Flight subcontracts these shoots; VirTour's retail price is the basis and
 * Roto Flight is paid a percentage split per line item, so these numbers must
 * match their live form exactly — never "corrected" to fit a pattern.
 *
 * TWO PRICE SETS
 * VirTour repriced nearly everything on 2026-09-21 (packages +40-100%, stills
 * +55%, video +60-80%; Zillow Showcase was the one cut). Both sets ship:
 *
 * - `current` — the live form, last checked 2026-11-04. This is what `flat`,
 *   `tiers`, `addonPrice` and `name` carry, so every unversioned read gets
 *   today's price. It is the 2026-09-21 reprice plus a second round found on
 *   2026-11-04 (FPV Video Tour, Video Clips, Twilight Video, Luxury Property
 *   Reel, both Social packages, the reel's add-on price, two regPrice ladders,
 *   and their Highlight ladder errors corrected). No saved quote was on the
 *   current set for any of those, so moving it in place repriced nothing.
 * - `legacy` — the 2026-05 form, under a `legacy: {}` block, present ONLY where
 *   that field actually differs. A product with no `legacy` block never changed,
 *   and both versions resolve to the same number.
 *
 * Quotes pick a version PER LINE (`line.priceVersion`), because an order placed
 * before the reprice still bills at the old sheet. A quote with no recorded
 * version is legacy by definition — it was quoted before this file gained a
 * current set. Read prices through the accessors below, never off the fields.
 *
 * Product ids (`vtPid`/`vtPids`/`vtAddonPid`) are VirTour's own and did NOT
 * change in the reprice, so they are shared by both versions.
 *
 * Pricing models:
 * - Tiered: price based on property square footage (10 tiers)
 * - Flat: fixed price regardless of size
 * - Addon: `addonPrice` is the price when bought alongside another service
 * - Per image: `perImage` products multiply by an image count
 * - `addedIn: 'current'`: launched after the legacy set; has no legacy price and
 *   cannot be sold at one.
 *
 * Tier index mapping (sq ft) — identical structure to serviceCatalog.js:
 *   0: 0–999, 1: 1,000–1,999, 2: 2,000–2,999, 3: 3,000–3,999, 4: 4,000–4,999,
 *   5: 5,000–5,999, 6: 6,000–6,999, 7: 7,000–7,999, 8: 8,000–8,999,
 *   9: 9,000–9,999 (VirTour's top rung — anything 10,000+ clamps here)
 *
 * Every product carries `vtPid` (and `vtPids` for tiered ladders) — VirTour's
 * own product id, used by docs/agent-notes/virtour-pricing/check-drift.mjs to
 * re-scrape the live form and flag price drift against the CURRENT set.
 *
 * Curation notes (source data: docs/agent-notes/virtour-pricing/):
 * - Their raw "A La Carte Services" category (pids 25173, 390566, 390538) is
 *   excluded — hidden platform duplicates of Matterport.
 * - Add-ons repeated under every package category at an identical price are
 *   listed once, preferring the "Real Estate Photography" pid.
 * - "+"-prefixed rows are the with-another-service price of the same-stem
 *   standalone product; those pairs are merged into one product carrying both
 *   `flat` (standalone) and `addonPrice` (`vtAddonPid` tracks the second pid).
 * - Legacy duplicate "Virtual Staging" (pid 17132, $50) is dropped in favour of
 *   "Virtually Staged Image" (pid 391164, same price).
 *
 * Their data errors are mirrored, not fixed (the rule above). Three packages
 * (GTS, Signature, MLS+) print a `regPrice` BELOW the sale price on some
 * rungs; that inversion is theirs and is carried verbatim. (The Highlight
 * ladder's two broken rungs, $2,100 and $1,700, were fixed on their side by
 * 2026-11-04.)
 *
 * Retired: Individual Lifestyle Shoot, Team Photoshoot and Lifestyle Video were
 * gone from their form on 2026-11-04. They stay here, flagged `retired`, so
 * older quotes still resolve; check-drift.mjs skips them.
 */

/** The two price sets, newest first. `id` is what a quote line stores. */
export const VT_PRICE_VERSIONS = [
  { id: 'current', label: 'Current', sub: 'Nov 2026', asOf: '2026-11-04' },
  { id: 'legacy', label: 'Old', sub: 'May 2026', asOf: '2026-05-01' },
];

export const VT_CURRENT_VERSION = 'current';
export const VT_LEGACY_VERSION = 'legacy';

/** Anything that isn't an explicit 'current' reads as legacy (see header). */
export function vtNormalizeVersion(version) {
  return version === VT_CURRENT_VERSION ? VT_CURRENT_VERSION : VT_LEGACY_VERSION;
}

export const VIRTOUR_DB = {
  tierLabels: [
    '0 – 999 sq ft', '1,000 – 1,999 sq ft', '2,000 – 2,999 sq ft', '3,000 – 3,999 sq ft',
    '4,000 – 4,999 sq ft', '5,000 – 5,999 sq ft', '6,000 – 6,999 sq ft', '7,000 – 7,999 sq ft',
    '8,000 – 8,999 sq ft', '9,000 – 9,999 sq ft',
  ],

  packages: [
    {
      id: 'vtpkg_gts', name: 'GTS Package (Guaranteed-To-Sell)',
      desc: 'Their flagship bundle: stills, twilight, Matterport, luxury film, reel, FPV and website.',
      tiers: [
        { price: 11000, regPrice: 10904, pid: '389174' },
        { price: 11500, regPrice: 10905, pid: '389489' },
        { price: 12000, regPrice: 11305, pid: '390029' },
        { price: 12500, regPrice: 12329, pid: '390014' },
        { price: 13000, regPrice: 12804, pid: '390022' },
        { price: 13500, regPrice: 13279, pid: '390023' },
        { price: 14000, regPrice: 13754, pid: '390026' },
        { price: 14500, regPrice: 14229, pid: '390027' },
        { price: 15000, regPrice: 14704, pid: '390028' },
        { price: 15500, regPrice: 14454, pid: '390596' },
      ],
      legacy: {
        tiers: [
          { price: 6977, regPrice: 7977 },
          { price: 7477, regPrice: 7828 },
          { price: 4499, regPrice: 8078 },
          { price: 5499, regPrice: 8877 },
          { price: 5999, regPrice: 9177 },
          { price: 6499, regPrice: 9477 },
          { price: 6999, regPrice: 9777 },
          { price: 7499, regPrice: 10077 },
          { price: 7999, regPrice: 10377 },
          { price: 8499, regPrice: 10027 },
        ],
      },
      includes: [
        'Still Photography', 'Aerial Photography', 'Twilight Photography',
        'Matterport 3D Virtual Tour', 'Add 2D Floor Plan', 'Luxury Property Video',
        'True Vertical Social Media Reel', 'Agent Narration Add-on', 'Video Clips',
        'FPV Video Tour', 'Property Website (Custom Domain w/ SSL Certificate)',
        'Inspire 3 Cinema Drone Upgrade',
      ],
    },
    {
      // Renamed on their form in the 2026-09 reprice; the legacy set keeps the
      // old label so a legacy-priced quote still matches the order it mirrors.
      id: 'vtpkg_ultimate', name: 'Signature Package',
      desc: 'Cinematic film, vertical reel, Matterport, floor plan, twilight and website.',
      tiers: [
        { price: 7000, regPrice: 5531, pid: '390537' },
        { price: 7350, regPrice: 5931, pid: '390837' },
        { price: 7700, regPrice: 6381, pid: '390846' },
        { price: 8050, regPrice: 6806, pid: '390848' },
        { price: 8400, regPrice: 7231, pid: '390849' },
        { price: 8750, regPrice: 7656, pid: '390850' },
        { price: 9100, regPrice: 8081, pid: '390945' },
        { price: 9450, regPrice: 8506, pid: '390950' },
        { price: 9800, regPrice: 8556, pid: '390952' },
        { price: 10150, regPrice: 9406, pid: '390953' },
      ],
      legacy: {
        name: 'Ultimate+ Package',
        tiers: [
          { price: 3413, regPrice: 3763 },
          { price: 3688, regPrice: 4063 },
          { price: 3963, regPrice: 4363 },
          { price: 4238, regPrice: 4663 },
          { price: 4513, regPrice: 4763 },
          { price: 4788, regPrice: 5263 },
          { price: 5063, regPrice: 5563 },
          { price: 5338, regPrice: 5863 },
          { price: 5363, regPrice: 5913 },
          { price: 5888, regPrice: 6463 },
        ],
      },
      includes: [
        'Still Photography', 'Aerial Photography', 'Cinematic Property Video',
        'True Vertical Social Media Reel', 'Matterport 3D Virtual Tour', 'Floor Plan',
        'Property Website (Custom Domain w/ SSL Certificate)', 'Twilight Photography',
      ],
    },
    {
      id: 'vtpkg_mls', name: 'MLS+ Package',
      desc: 'Stills, Matterport with 2D floor plan, highlight video and website.',
      tiers: [
        { price: 4000, regPrice: 3283, pid: '390757' },
        { price: 4250, regPrice: 3583, pid: '390755' },
        { price: 4500, regPrice: 3808, pid: '391005' },
        { price: 4750, regPrice: 4183, pid: '391010' },
        { price: 5000, regPrice: 4483, pid: '391015' },
        { price: 5250, regPrice: 4783, pid: '391018' },
        { price: 5500, regPrice: 5083, pid: '391023' },
        { price: 5750, regPrice: 5383, pid: '391024' },
        { price: 6000, regPrice: 5683, pid: '391025' },
        { price: 6250, regPrice: 5983, pid: '391026' },
      ],
      legacy: {
        tiers: [
          { price: 2249, regPrice: 2406 },
          { price: 2449, regPrice: 2606 },
          { price: 2649, regPrice: 2756 },
          { price: 2849, regPrice: 3006 },
          { price: 3049, regPrice: 3706 },
          { price: 3249, regPrice: 3406 },
          { price: 3449, regPrice: 3606 },
          { price: 3649, regPrice: 3806 },
          { price: 3849, regPrice: 4006 },
          { price: 4049, regPrice: 4206 },
        ],
      },
      includes: [
        'Still Photography', 'Aerial Photography', 'Matterport 3D Virtual Tour',
        'Add 2D Floor Plan', 'Property Highlight Video',
        'Property Website (Custom Domain w/ SSL Certificate)',
      ],
    },
    {
      id: 'vtpkg_3d', name: '3D Package',
      desc: 'Stills, aerials and a Matterport tour with a 2D floor plan.',
      tiers: [
        { price: 2000, regPrice: 2048, pid: '391028' },
        { price: 2200, regPrice: 2273, pid: '391029' },
        { price: 2400, regPrice: 2498, pid: '391030' },
        { price: 2600, regPrice: 2723, pid: '391033' },
        { price: 2800, regPrice: 2948, pid: '391034' },
        { price: 3000, regPrice: 3173, pid: '390968' },
        { price: 3200, regPrice: 3398, pid: '391035' },
        { price: 3400, regPrice: 3623, pid: '391036' },
        { price: 3600, regPrice: 3848, pid: '391037' },
        { price: 3800, regPrice: 4073, pid: '391038' },
      ],
      legacy: {
        tiers: [
          { price: 1399, regPrice: 1522 },
          { price: 1549, regPrice: 1672 },
          { price: 1699, regPrice: 1822 },
          { price: 1849, regPrice: 1972 },
          { price: 1999, regPrice: 2122 },
          { price: 2149, regPrice: 2272 },
          { price: 2299, regPrice: 2422 },
          { price: 2449, regPrice: 2572 },
          { price: 2599, regPrice: 2722 },
          { price: 2749, regPrice: 2872 },
        ],
      },
      includes: [
        'Still Photography', 'Aerial Photography', 'Matterport 3D Virtual Tour',
        'Add 2D Floor Plan',
      ],
    },
    {
      // Renamed from "Standard Package" in the 2026-09 reprice.
      id: 'vtpkg_standard', name: 'Essential Package',
      desc: 'Baseline listing coverage: still photography plus aerials.',
      tiers: [
        { price: 1500, regPrice: 1549, pid: '389340' },
        { price: 1650, regPrice: 1699, pid: '389886' },
        { price: 1800, regPrice: 1849, pid: '389887' },
        { price: 1950, regPrice: 1999, pid: '389973' },
        { price: 2100, regPrice: 2149, pid: '389978' },
        { price: 2250, regPrice: 2299, pid: '389979' },
        { price: 2400, regPrice: 2449, pid: '389980' },
        { price: 2550, regPrice: 2599, pid: '390064' },
        { price: 2700, regPrice: 2749, pid: '390066' },
        { price: 2850, regPrice: 2899, pid: '390068' },
      ],
      legacy: {
        name: 'Standard Package',
        tiers: [
          { price: 999, regPrice: 1048 },
          { price: 1099, regPrice: 1148 },
          { price: 1199, regPrice: 1248 },
          { price: 1299, regPrice: 1348 },
          { price: 1399, regPrice: 1448 },
          { price: 1499, regPrice: 1548 },
          { price: 1599, regPrice: 1648 },
          { price: 1699, regPrice: 1748 },
          { price: 1799, regPrice: 1848 },
          { price: 1899, regPrice: 1948 },
        ],
      },
      includes: ['Still Photography', 'Aerial Photography'],
    },
  ],

  categories: [
    {
      id: 'vt_photo', num: 1, name: 'Photography', color: '#06B3E8', icon: '📷',
      desc: 'Stills, aerials, twilight and image treatments.',
      products: [
        { id: 'vt_still_photography', name: 'Still Photography', vtPid: '38461',
          vtPids: ['38461', '390132', '390139', '390142', '390146', '390150', '390152', '390153', '390155', '390160'],
          tiers: [1200, 1350, 1500, 1650, 1800, 1950, 2100, 2250, 2400, 2550],
          legacy: { tiers: [749, 849, 949, 1049, 1149, 1249, 1349, 1449, 1549, 1649] } },
        { id: 'vt_aerial_photography', name: 'Aerial Photography', vtPid: '14164', flat: 349,
          legacy: { flat: 299 } },
        { id: 'vt_twilight_photography', name: 'Twilight Photography', vtPid: '11266', flat: 499,
          vtAddonPid: '25178', addonPrice: 349,
          legacy: { flat: 349, addonPrice: 299 } },
        { id: 'vt_virtual_twilight', name: 'Virtual Twilight Photography', vtPid: '19628', flat: 50 },
        { id: 'vt_sunrise_photography', name: 'Sunrise Photography', vtPid: '155600', flat: 349 },
        { id: 'vt_vignettes', name: 'Vignettes', vtPid: '357635', flat: 349,
          vtAddonPid: '357636', addonPrice: 299 },
        { id: 'vt_capture_neighborhood', name: 'Capture the Neighborhood', vtPid: '11265', flat: 199,
          vtAddonPid: '25179', addonPrice: 175 },
        { id: 'vt_property_lines', name: 'Property Lines', vtPid: '17370', flat: 25, perImage: true },
        { id: 'vt_location_markers', name: 'Location Markers', vtPid: '17371', flat: 25, perImage: true },
      ],
    },
    {
      id: 'vt_tours', num: 2, name: 'Virtual Tours & Floor Plans', color: '#7B5EA7', icon: '🗺',
      desc: 'Matterport, floor plans, Zillow Showcase and panoramas.',
      products: [
        { id: 'vt_matterport', name: 'Matterport 3D Virtual Tour', vtPid: '390758',
          vtPids: ['390758', '390756', '390539', '390540', '390541', '390544', '390545', '390546', '390547', '390548'],
          tiers: [399, 474, 549, 624, 699, 774, 849, 924, 999, 1074],
          legacy: { tiers: [399, 449, 499, 549, 599, 649, 699, 749, 799, 849] } },
        { id: 'vt_floor_plan', name: 'Floor Plan (2D)', vtPid: '390554',
          vtPids: ['390554', '390556', '390557', '390558', '390559', '390957', '390560', '390561', '390562', '390563'],
          tiers: [149, 199, 249, 299, 349, 399, 449, 499, 549, 649],
          legacy: { tiers: [134, 184, 234, 284, 334, 384, 434, 484, 534, 584] } },
        { id: 'vt_zillow_showcase', name: 'Zillow Showcase', vtPid: '390599',
          vtPids: ['390599', '390598', '390567', '390580', '390584', '390586', '390587', '390588', '390589', '390590'],
          tiers: [199, 249, 299, 349, 399, 449, 499, 549, 599, 649],
          legacy: { tiers: [249, 299, 349, 399, 449, 499, 549, 599, 649, 699] } },
        { id: 'vt_add_2d_floor_plan', name: 'Add 2D Floor Plan', vtPid: '276308', flat: 100,
          legacy: { flat: 75 } },
        { id: 'vt_add_3d_floor_plan', name: 'Add 3D Floor Plan', vtPid: '25297', flat: 75 },
        { id: 'vt_upgrade_3d_floor_plan', name: 'Upgrade to 3D Floor Plan', vtPid: '26134', flat: 75 },
        { id: 'vt_site_plan', name: 'Site Plan', vtPid: '133573', flat: 595,
          legacy: { flat: 399 } },
        { id: 'vt_adu_floor_plan', name: 'Guest House/ADU Floor Plan', vtPid: '26130', flat: 184 },
        { id: 'vt_adu_2d_floor_plan', name: '2D Floor Plan for Guest House/ADU', vtPid: '17789', flat: 50 },
        { id: 'vt_panoramic_view', name: 'Panoramic View', vtPid: '82834', flat: 199 },
      ],
    },
    {
      id: 'vt_film', num: 3, name: 'Film Production', color: '#C94A2A', icon: '🎬',
      desc: 'Highlight, cinematic and luxury property films plus film add-ons.',
      products: [
        { id: 'vt_highlight_video', name: 'Property Highlight Video', vtPid: '20718',
          vtPids: ['20718', '390597', '390601', '390603', '390602', '390604', '390605', '390606', '390611', '390612'],
          tiers: [1100, 1175, 1250, 1325, 1400, 1475, 1550, 1625, 1700, 1775],
          legacy: { tiers: [749, 799, 849, 899, 1449, 999, 1049, 1099, 1149, 1199] } },
        { id: 'vt_cinematic_video', name: 'Cinematic Property Video', vtPid: '25172',
          vtPids: ['25172', '390759', '390760', '390761', '390762', '390839', '390937', '390942', '390943', '390944'],
          tiers: [1950, 2100, 2250, 2400, 2550, 2700, 2850, 3000, 3150, 3300],
          legacy: { tiers: [1049, 1149, 1249, 1349, 1249, 1549, 1649, 1749, 1849, 1949] } },
        { id: 'vt_luxury_video', name: 'Luxury Property Video', vtPid: '59205',
          vtPids: ['59205', '390613', '390615', '390627', '390628', '390629', '390631', '390632', '390633', '390634'],
          tiers: [5500, 5750, 6000, 6250, 6500, 6750, 7000, 7250, 7500, 7750],
          legacy: { tiers: [3499, 3649, 3799, 3949, 4099, 4249, 4399, 4549, 4699, 4849] } },
        { id: 'vt_drone_video', name: 'Drone Property Video', vtPid: '59223', flat: 499 },
        { id: 'vt_full_production_film', name: 'Full Production Property Film', vtPid: '255645', flat: 9500,
          desc: 'Offered only on properties 4,000 sq ft and up.' },
        { id: 'vt_inspire3_upgrade', name: 'Inspire 3 Cinema Drone Upgrade', vtPid: '155603', flat: 500 },
        { id: 'vt_twilight_video', name: 'Twilight Video', vtPid: '185268', flat: 500,
          legacy: { flat: 299 } },
        { id: 'vt_location_video', name: 'Location Video', vtPid: '17126', flat: 249 },
        { id: 'vt_agent_narration', name: 'Agent Narration Add-on', vtPid: '17125', flat: 175 },
        { id: 'vt_branded_version', name: 'Branded Video Version', vtPid: '161238', flat: 50 },
        { id: 'vt_upgrade_highlight_cinematic', name: 'Upgrade Highlight Reel to Cinematic Property Video', vtPid: '161299', flat: 249 },
        { id: 'vt_upgrade_highlight_luxury', name: 'Upgrade Highlight Reel to Luxury Property Video', vtPid: '82816', flat: 749 },
        { id: 'vt_upgrade_cinematic_luxury', name: 'Upgrade Cinematic Video to Luxury Property Video', vtPid: '82818', flat: 499 },
      ],
    },
    {
      id: 'vt_social', num: 4, name: 'Social Media', color: '#2E8B57', icon: '📱',
      desc: 'Vertical reels, clips, agent-guided tours and FPV.',
      products: [
        { id: 'vt_vertical_reel', name: 'True Vertical Social Media Reel', vtPid: '262188', flat: 850,
          vtAddonPid: '229946', addonPrice: 799,
          legacy: { flat: 649, addonPrice: 599 } },
        { id: 'vt_video_clips', name: 'Video Clips', vtPid: '103347', flat: 850,
          vtAddonPid: '103380', addonPrice: 399,
          legacy: { flat: 399, addonPrice: 249 } },
        { id: 'vt_optimized_vertical_reel', name: 'Optimized Vertical Reel', vtPid: '82826', flat: 174 },
        { id: 'vt_luxury_reel', name: 'Luxury Property Reel', vtPid: '103349', flat: 4999,
          legacy: { flat: 1999 } },
        { id: 'vt_agent_guided_tour', name: 'Agent-Guided Property Tour', vtPid: '104660', flat: 1000,
          vtAddonPid: '104783', addonPrice: 750 },
        { id: 'vt_fpv_tour', name: 'FPV Video Tour', vtPid: '161297', flat: 1500,
          vtAddonPid: '279618', addonPrice: 999, legacy: { flat: 1249 } },
        { id: 'vt_fpv_clips', name: 'FPV Clips', vtPid: '388175', flat: 499 },
        // Launched with the 2026-09 reprice — no legacy price exists.
        { id: 'vt_social_starter', name: 'Social Starter', vtPid: '433543', flat: 1999,
          addedIn: 'current', desc: '1 Reel + 2 Stories' },
        { id: 'vt_social_pro', name: 'Social Pro', vtPid: '433544', flat: 7999,
          addedIn: 'current',
          desc: '2 Reels + 1 Luxury Reel (cinema cameras, cinema drone, FPV, day to twilight) + Agent Narration + 3 Stories' },
      ],
    },
    {
      id: 'vt_web', num: 5, name: 'Websites & Extras', color: '#2E7D9B', icon: '🌐',
      desc: 'Property websites, hosting renewals and print collateral.',
      products: [
        { id: 'vt_website_custom_domain', name: 'Property Website (Custom Domain w/ SSL Certificate)', vtPid: '20723', flat: 135 },
        { id: 'vt_website_vtm_domain', name: 'Property Website (VirTour Media Domain)', vtPid: '20719', flat: 99 },
        { id: 'vt_website_standard_domain', name: 'Property Website (Standard Domain)', vtPid: '20722', flat: 100 },
        { id: 'vt_hosting_year_website', name: 'Additional Year of Website Hosting', vtPid: '82821', flat: 35 },
        { id: 'vt_hosting_year_cloud', name: 'Additional Year of Cloud Hosting', vtPid: '76461', flat: 75 },
        { id: 'vt_flyers_package', name: 'Custom Listing Flyers (package)', vtPid: '20721', flat: 200 },
        { id: 'vt_flyers_photo', name: 'Custom Listing Flyers (with photography)', vtPid: '20724', flat: 100 },
      ],
    },
    {
      id: 'vt_staging', num: 6, name: 'Staging & Lifestyle', color: '#B8860B', icon: '🛋',
      desc: 'Virtual staging and renovation renders, agent and team shoots.',
      products: [
        { id: 'vt_virtually_staged_image', name: 'Virtually Staged Image', vtPid: '391164', flat: 50, perImage: true },
        { id: 'vt_virtually_renovated_image', name: 'Virtually Renovated Image', vtPid: '391165', flat: 100, perImage: true },
        { id: 'vt_lifestyle_shoot', name: 'Individual Lifestyle Shoot', vtPid: '22586', flat: 650, retired: '2026-11-04' },
        { id: 'vt_team_photoshoot', name: 'Team Photoshoot', vtPid: '23087', flat: 1150, retired: '2026-11-04' },
        { id: 'vt_lifestyle_video', name: 'Lifestyle Video', vtPid: '23086', flat: 300, retired: '2026-11-04' },
      ],
    },
  ],
};

/**
 * Square footage to tier index. Same 1,000 sq ft steps as serviceCatalog.js,
 * clamped to VirTour's top rung.
 */
export function vtTierIndex(sqft = 0) {
  return Math.min(Math.max(0, Math.floor(sqft / 1000)), 9);
}

/**
 * The price-carrying face of a product or package at a version: the item itself
 * for 'current', its `legacy` block overlaid on it otherwise. Fields the legacy
 * block omits (because they never changed) fall through to the current value.
 */
function vtFace(item, version) {
  if (!item) return item;
  if (vtNormalizeVersion(version) === VT_CURRENT_VERSION) return item;
  return item.legacy ? { ...item, ...item.legacy } : item;
}

/**
 * False when the item did not exist at that version, so it cannot be sold at
 * one. Only products launched after the legacy set (`addedIn: 'current'`).
 */
export function vtExistsAt(item, version) {
  if (!item) return false;
  if (vtNormalizeVersion(version) === VT_CURRENT_VERSION) return true;
  return item.addedIn !== VT_CURRENT_VERSION;
}

/** True when the item's price actually differs between the two sets. */
export function vtPriceChanged(item, sqft = 0) {
  if (!item || !vtExistsAt(item, VT_LEGACY_VERSION)) return false;
  const isPkg = Array.isArray(item.includes);
  if (isPkg) {
    return vtGetPackagePrice(item, sqft, VT_CURRENT_VERSION).price
      !== vtGetPackagePrice(item, sqft, VT_LEGACY_VERSION).price;
  }
  return vtGetPrice(item, sqft, VT_CURRENT_VERSION) !== vtGetPrice(item, sqft, VT_LEGACY_VERSION)
    || getVtAddonPrice(item, sqft, VT_CURRENT_VERSION) !== getVtAddonPrice(item, sqft, VT_LEGACY_VERSION);
}

/** Display name at a version — two packages were renamed in the reprice. */
export function vtGetName(item, version) {
  return vtFace(item, version)?.name ?? '';
}

/**
 * Retail price for a product at a given square footage and price version.
 * Returns a number — flat products ignore sqft.
 */
export function vtGetPrice(product, sqft = 0, version = VT_CURRENT_VERSION) {
  const face = vtFace(product, version);
  if (face.flat !== undefined) return face.flat;
  return face.tiers[vtTierIndex(sqft)];
}

/**
 * Price for a product bought alongside another service. Falls back to the
 * standalone price: add-on-only products carry their add-on price as `flat`.
 */
export function getVtAddonPrice(product, sqft = 0, version = VT_CURRENT_VERSION) {
  const face = vtFace(product, version);
  if (face.addonPrice !== undefined) return face.addonPrice;
  return vtGetPrice(product, sqft, version);
}

/**
 * VirTour's own product id for the rung that applies at a given square footage.
 * `asAddon` resolves the "+"-prefixed sibling pid where one exists. Product ids
 * are shared by both price versions, so this takes no version.
 */
export function vtGetPid(product, sqft = 0, asAddon = false) {
  if (asAddon && product.vtAddonPid) return product.vtAddonPid;
  if (product.vtPids) return product.vtPids[vtTierIndex(sqft)];
  return product.vtPid;
}

/**
 * Retail price for a package at a given square footage and price version.
 * Returns { price, regPrice, pid } — `pid` always comes from the current tiers,
 * which both versions share.
 */
export function vtGetPackagePrice(pkg, sqft = 0, version = VT_CURRENT_VERSION) {
  const idx = vtTierIndex(sqft);
  const tier = vtFace(pkg, version).tiers[idx];
  return { ...tier, pid: pkg.tiers[idx].pid };
}

/**
 * Find a product by its ID across all categories.
 * Returns { category, product } or null.
 */
export function findVtProduct(productId) {
  for (const cat of VIRTOUR_DB.categories) {
    const product = cat.products.find((p) => p.id === productId);
    if (product) return { category: cat, product };
  }
  return null;
}

/**
 * Find a package by its ID. Returns the package or null.
 */
export function findVtPackage(packageId) {
  return VIRTOUR_DB.packages.find((p) => p.id === packageId) ?? null;
}
